import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled'
import { mediaQueries } from 'styled/core'

import { createFormBag, FormBag, FormData, FormErrors, validateFormBag } from 'react-formage'

import { actionCreators, GettyActionCreators } from '../../../actions'

import { GettyStore } from '../../../store'

import { categorySchema } from '../../../schemas/category'

import { connect } from 'containers/store'
import { sharedActionCreators } from 'containers/shared'

import { Fieldset } from 'components/ui/fieldset'
import { FooterEditEntity, FOOTER_HEIGHT } from 'components/ui/footer'
import { NAVBAR_HEIGHT } from 'components/ui/primary-navigation'
import { PrimaryNavigationEditEntity } from 'components/ui/primary-navigation/edit-entity'
import { StatusGuard } from 'components/ui/status-guard'
import { Input } from 'components/ui/input'
import { Description, Title } from 'components/ui/list-item-label'
import { SelectFilter, SelectFilterItem } from 'components/ui/select-filter'
import { Confirm } from 'components/ui/modal'

import { LayoutFullWidthPage } from 'layouts/full-width-page'

import { TourCategory } from 'generated/mos/org/getty/audio'
import { UpdateTourCategoryRequest, UpdateTourCategoriesRequest } from 'generated/mos/org/getty/audiomanagement'
import { Language } from 'generated/mos/i18n'
import { Site } from 'generated/mos/structure'
import { TranslatableStringValue } from 'generated/mos/translatable'

import { pick, areObjectsEqual } from 'helpers/core'
import { statusSelector, Status } from 'helpers/status'
import { space } from 'helpers/style'
import { TranslationFormBags } from 'helpers/schema'
import { isEnglish, parseLanguage } from 'helpers/i18n'
import { LinkableValue } from 'helpers/linkables'
import { SecondaryNavigation } from 'components/ui/secondary-navigation'
import { getSubnav } from '../subnav'

import { DEFAULT_SITE_REF_ID, LANGUAGES, LanguageCode } from '../../../constants'

type ActionProps =
  Pick<GettyActionCreators, 'gettyListTourCategoriesRequest' | 'gettyListSitesRequest' | 'gettyUpdateTourCategoriesRequest'> &
  Pick<typeof sharedActionCreators, 'toastNotification'>;

type ConnectedProps = Pick<GettyStore['getty'], 'sites' | 'categories'>;

type DirectProps = {
  readonly historyPush: (id: string) => void;
  readonly projectId: string;
};

type Props = ActionProps & ConnectedProps & DirectProps;

type FormValues = {
  [key: string]: string | undefined;
}


type FormProps = {
  language: Language.Entity;
  formBag: FormBag<FormValues> | undefined;
  setFormBag: (bag: FormBag<FormValues>) => void;
}

const HEADER_HEIGHT = 100
const FILTER_HEIGHT = 50

const FilterButtonRow = styled.div`
  display: flex;
  height: ${FILTER_HEIGHT}px;
  justify-content: space-between;
  padding-top: ${space(2)};
`;

const Scroll = styled.div`
  display: flex;
  padding-top: ${space(6)};
  padding-bottom: ${space(15)};
  overflow-x: scroll;
  height: calc(100vh - ${(NAVBAR_HEIGHT + FOOTER_HEIGHT + HEADER_HEIGHT + FILTER_HEIGHT)}px);
`
const Panels = styled.div`
  display: flex;
  flex-wrap: nowrap;
  flex-direction: row;
  padding-left: ${space(9)};
  padding-right: ${space(9)};


  ${mediaQueries.lap} {
    padding-left: ${space(18)};
    padding-right: ${space(18)};
  }

  @media only screen and (min-width: 1600px) {
    padding-left: calc(${space(18)} + (100% - 1600px) / 2);
    padding-right: calc(${space(18)} + (100% - 1600px) / 2);
  }
`

const Panel = styled.div`
  width: calc(100vw - 36px);
  flex: 0 0 auto;
  &:not(:last-child) {
    padding-right: ${space(6)};
    margin-right: ${space(6)};
    border-right: 1px solid ${({ theme }) => theme.color.grayL60};
  }

  ${mediaQueries.hand} {
    width: 500px;
  }
`

const CategoryHeader = styled.div`
  height: ${HEADER_HEIGHT}px;
  display: flex;
  width: 100%;
  align-items: center;
  padding-top: ${space(8)};
  padding-bottom: ${space(6)};
  border-bottom: 1px solid ${({ theme }) => theme.color.grayL60};
`

const TranslationForm = (props: FormProps) => {
  const { formBag, language, setFormBag } = props
  const isDisabled = isEnglish(language)
  return formBag ? (
    <Panel>
      <FormData
        bag={formBag}
        onUpdate={(event) => {
          setFormBag(event.bag)
        }}
        validate={() => ({})}
        >
        <Fieldset legend={parseLanguage(language)}>
          {Object.entries(formBag.values).map(([key, value], index) => (
            <Input
              key={key}
              label={`Category ${index + 1}`}
              field={key}
              isDisabled={isDisabled}
            />
          ))}
        </Fieldset>
      </FormData>
    </Panel>
  ) : null
}

const CategoryTranslationsView = (props: Props) => {
  const {
    gettyListTourCategoriesRequest ,
    gettyListSitesRequest,
    gettyUpdateTourCategoriesRequest,
    historyPush,
    projectId,
    categories,
    toastNotification,
    sites,
  } = props;

  // set up form values
  const [ initialValues, setInitialValues ] = useState<TranslationFormBags<FormValues, LanguageCode> | undefined>(undefined);
  const [ formBags, setFormBags ] = useState<TranslationFormBags<FormValues, LanguageCode> | undefined>(undefined);
  const [ selectedSite, setSelectedSite ] = useState<Site.Entity | undefined>(undefined)
  const [ siteWarning, setSiteWarning ] = useState<Site.Entity| undefined>(undefined)

  const backLocation = `/project/${projectId}/tours`;

  const categoryData = statusSelector.data(categories);
  const tourCategories = categoryData && categoryData.tourCategories;

  const hasSubmitted = useRef(false)

  useEffect(() => {
    gettyListSitesRequest()
  }, [gettyListSitesRequest])

  useEffect(() => {
    if(selectedSite && selectedSite.ref) {
      gettyListTourCategoriesRequest(selectedSite.ref)
    }
  }, [gettyListTourCategoriesRequest, selectedSite])


  useEffect(() => {
    if(categories.status === Status.Failed) {
      setInitialValues(undefined)
      setFormBags(undefined)
    }

  }, [gettyListTourCategoriesRequest, categories, selectedSite])

  useEffect(() => {
    if(sites.status === Status.Ready && sites.data.sites && selectedSite === undefined) {
      const defaultSite = sites.data.sites.filter(site => Site.mustRef(site.ref).id === DEFAULT_SITE_REF_ID)[0] || sites.data.sites[0]
      setSelectedSite(defaultSite)
    }
  }, [sites.status, sites, selectedSite])

  useEffect(() => {
    const getInitialValues = () => {
      const initialValues = LANGUAGES.reduce(
        (acc, curr) => {
          const initialFormValues = tourCategories && tourCategories.reduce((accumulator, current, index) => {
            return {
              ...accumulator,
              [`category${index}`]: categorySchema.name.read(current.name, curr) as string | undefined
            }
          }, {})
          return {
            ...acc,
            [curr.languageCode]: createFormBag(initialFormValues)
          }
        // eslint-disable-next-line @typescript-eslint/no-object-literal-type-assertion
        }, {} as TranslationFormBags<FormValues, LanguageCode>)
      return initialValues
    }
    if (categories && categories.status === Status.Ready) {
      const initValues = getInitialValues()
      setInitialValues(initValues)
      setFormBags(initValues)
    }

    if (categories.status === Status.Ready && hasSubmitted.current) {
      toastNotification({ type: 'success', text: 'Translations updated' })
      hasSubmitted.current = false
    }

  }, [tourCategories, categories, categories.status]) // eslint-disable-line react-hooks/exhaustive-deps

  const siteOpts = sites.status === Status.Ready && sites.data.sites
      ? sites.data.sites.map((site: Site.Entity) => ({
        label: site.name,
        value: site,
      }))
      : []

  const modified = initialValues && formBags && !areObjectsEqual(initialValues, formBags)

  const validateForm = (values: FormValues) => {
    const errors: FormErrors<FormValues> = {}
    return errors
  }

  const validate = (formBags: TranslationFormBags<FormValues, LanguageCode>): boolean => {
    let valid = true;
    LANGUAGES.forEach((value) => {
      const languageCode: LanguageCode = value.languageCode as LanguageCode
      const bag = validateFormBag(formBags[languageCode], validateForm);
      valid = bag.valid
      updateFormBags(languageCode as LanguageCode)(bag)
    })
    return valid
  }

  const submit = (formBags: TranslationFormBags<FormValues, LanguageCode>, tourCategories: ReadonlyArray<TourCategory.Entity>) => {
    // Validate all bags
    const valid = validate(formBags)

    // Check if any bags a invalid
    if(!valid) return false;

    // Assemble Payload
    const translatedTourCategories = tourCategories.map((item, index) => {
      const category = LANGUAGES.reduce((acc: ReadonlyArray<TranslatableStringValue.TranslatedValue.Entity>, curr: Language.Entity) => {
        const languageCode = curr.languageCode as keyof TranslationFormBags<FormValues, LanguageCode>
        const { values } = formBags[languageCode]
        const write = categorySchema.name.write(values[`category${index}`] as any as LinkableValue, item.name, curr)
        if(write === undefined) return acc
        const value = write.translatedValues.find((value: any) => value.language && value.language.languageCode === languageCode)
        return [...acc, value]
      }, [])
      return category
    })

    // Update tour categories.
    const tourCategoryUpdates = translatedTourCategories && tourCategories.map(
      ({ ref, tourRefs }: TourCategory.Entity, index): UpdateTourCategoryRequest.Entity => ({
        ...UpdateTourCategoryRequest.defaults,
        name: {
          type: "mos.translatable.TranslatableStringValue",
          translatedValues: translatedTourCategories[index],
        },
        tourRefs: tourRefs,
        tourCategoryRef: ref,
        updateMask: { ...UpdateTourCategoryRequest.defaults.updateMask, fields: ['name'] },
      })
    )
    gettyUpdateTourCategoriesRequest({
      ...UpdateTourCategoriesRequest.defaults,
      tourCategoryUpdates,
    })

    hasSubmitted.current = true

    return false;
  };

  const updateFormBags= (languageCode: LanguageCode) =>  (bag: FormBag<FormValues>) => {
    if(formBags) {
      setFormBags({
        ...formBags,
        [languageCode]: bag
      })
    }
  }

  return (
    <>
      <LayoutFullWidthPage
        nav={() => (
          <PrimaryNavigationEditEntity
            title={`Translate categories`}
            onConfirm={modified ? () => historyPush(backLocation) : undefined}
            navBackRoute={backLocation}
          />
        )}
        subnav={() => (
          <SecondaryNavigation navItems={getSubnav(projectId)} modified={modified} historyPush={historyPush} >
          </SecondaryNavigation>
        )}
        title="Categories"
        content={() => (
          <>
            {siteWarning &&
              <Confirm
                onClose={() => setSiteWarning(undefined) }
                title="Unsaved Changes"
                message="Do you want to discard your changes?"
                confirmLabel="Yes, discard"
                closeLabel="Cancel"
                onConfirm={() => {
                  setSelectedSite(siteWarning)
                  setSiteWarning(undefined)
                }}
              />
            }
            <StatusGuard status={categories} mode="form">
              <Scroll>
                <Panels>
                  {formBags && LANGUAGES.map((value) => {
                    const languageCode: LanguageCode = value.languageCode as LanguageCode
                    return (
                      <TranslationForm key={languageCode} language={value} formBag={formBags[languageCode]} setFormBag={updateFormBags(languageCode)} />
                    )
                  })}
                </Panels>
              </Scroll>
            </StatusGuard>
          </>
        )}
        footer={() => formBags && tourCategories && (
          <FooterEditEntity
            entity={categories}
            modified={modified}
            onCancel={() => historyPush(backLocation)}
            onSave={() => {
              submit(formBags, tourCategories);
              return false;
            }}
          />
        )}
      />
    </>
  )
}

export const CategoryTranslations = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  store => pick(store.getty, 'sites', 'categories'),
  {
    ...pick(actionCreators, 'gettyListTourCategoriesRequest', 'gettyListSitesRequest', 'gettyUpdateTourCategoriesRequest'),
    ...pick(sharedActionCreators, 'toastNotification'),
  },
)(CategoryTranslationsView);
